import React, { useCallback, useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Button, Dialog, DialogContent, DialogTitle, Slide, useMediaQuery, useTheme } from '@mui/material';

import { TransitionProps } from '@mui/material/transitions';
import { StyleSheet } from 'react-native';
import x from '../../../asset/images/icons/es_x_white.svg';
import { useThemeContext } from '../../../hook/useThemeManager';
import { ColorScheme, theme } from '../../../constants/theme';
import { isEmpty } from 'lodash';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';
import { CustomTextField } from '../../TextInput/CustomTextField';
import { IThspianPlaylist } from '../../../types/global/helper';

export interface IPlaylistDetails {
    name: string;
    description: string;
}

interface IPlaylistEditor {
    onSubmit: (details: IPlaylistDetails) => void;
    onClose?: () => void;
    open: boolean;
    title?: string;
    initialValue?: IThspianPlaylist;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlaylistEditor: React.FC<IPlaylistEditor> = ({ onClose, onSubmit, open, title, initialValue }) => {
    const { themeStyle } = useThemeContext();
    const themeui = useTheme();
    const fullScreen = useMediaQuery(themeui.breakpoints.down('md'));
    const styles = dynamicStyles(themeStyle);
    const [details, setDetails] = useState<IPlaylistDetails>({
        name: '',
        description: '',
    });

    const handleClose = () => {
        onClose && onClose();
    };

    const handleSubmit = () => {
        onSubmit(details);
        setDetails({
            name: '',
            description: '',
        });
        handleClose();
    };
    const updateForm = useCallback((field: string, value: any) => {
        setDetails(prevState => ({
            ...prevState,
            [field]: value,
        }));
    }, []);

    useEffect(() => {
        if (initialValue) {
            setDetails({
                name: initialValue.name,
                description: initialValue.description,
            });
        }
    }, [initialValue]);

    const disableButton = isEmpty(details.name);

    const renderMenu = (
        <Dialog
            scroll="paper"
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            fullWidth={false}
            fullScreen={false}
            maxWidth={'lg'}
            PaperProps={{
                style: {
                    backgroundColor: fullScreen
                        ? theme[themeStyle].background_gradient.mid
                        : theme[themeStyle].background_gradient.start, // make this a color variable
                    width: '90vw',
                    maxWidth: '500px',
                    borderRadius: 20,
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    backdropFilter: 'blur(8px)',
                    margin: 0,
                },
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                backgroundColor: theme[themeStyle].modal_dark,
            }}>
            <DialogTitle>
                <Box
                    sx={{
                        width: '100%',
                        height: 'fit-content',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <Typography style={styles.title}>{title}</Typography>
                    <IconButton style={styles.close} onClick={handleClose} sx={{ '&:hover': { cursor: 'pointer' } }}>
                        <CustomLazyLoadImage src={x} alt="close button" />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box
                    style={{
                        padding: '8px',
                        minWidth: '150px',
                    }}>
                    <>
                        <CustomTextField
                            label="Name the playlist"
                            textFieldProps={{
                                InputProps: {
                                    disableUnderline: true,
                                    placeholder: 'e.g Mixed monolgue...',
                                },
                                multiline: true,
                                rows: 1,
                                type: 'text',
                                style: styles.input,

                                inputProps: {
                                    style: {
                                        color: theme[themeStyle].lightWhite,
                                        height: '100%',
                                        padding: '10px',
                                    },
                                },

                                value: details['name'],
                                onChange: e => updateForm('name', e.target.value),
                            }}
                        />
                        <CustomTextField
                            label="Description"
                            textFieldProps={{
                                InputProps: {
                                    disableUnderline: true,
                                    placeholder: 'e.g Submission from my contest...',
                                },
                                multiline: true,
                                rows: 5,
                                type: 'text',
                                style: styles.input,
                                inputProps: {
                                    style: {
                                        color: theme[themeStyle].lightWhite,
                                        height: '100%',
                                        paddingRight: '14px',
                                        paddingLeft: '14px',
                                        paddingTop: '14px',
                                    },
                                },
                                value: details['description'],
                                onChange: e => updateForm('description', e.target.value),
                            }}
                        />
                    </>

                    <Button
                        onClick={handleSubmit}
                        disabled={disableButton}
                        style={{
                            height: '52px',
                            borderRadius: 100,
                            fontSize: 16,
                            fontWeight: '600',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                        sx={{
                            textDecoration: 'none',
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                            border: disableButton ? 'transparent' : `2px solid ${theme[themeStyle].primary_orange}`,
                            color: disableButton ? theme[themeStyle].accountGray : theme[themeStyle].pure_black,
                            textTransform: 'none',
                            width: '100%',
                            '&:hover': {
                                border: `2px solid ${theme[themeStyle].primary_orange}`,
                                backgroundColor: theme[themeStyle].pure_black,
                                color: theme[themeStyle].primary_orange,
                            },
                            my: 3,
                        }}>
                        <Typography
                            style={{
                                fontFamily: FONT_ENUM.MEDIUM_HEADER,
                                color: disableButton ? theme[themeStyle].darkGray60 : theme[themeStyle].primary_white,
                            }}>
                            Submit
                        </Typography>
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );

    return <>{renderMenu}</>;
};

export { PlaylistEditor };

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        close: {
            width: 'fit-content',
            height: 'fit-content',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            position: 'absolute',
            right: '10px',
            top: '10px',
        },
        title: {
            textAlign: 'left',
            fontSize: 24,
            fontStyle: 'normal',
            fontWeight: '600',
            color: theme[colorScheme].primary_white,
            fontFamily: FONT_ENUM.MAIN_BOLD,
            textTransform: 'capitalize',
        },
        button: {
            fontFamily: FONT_ENUM.MEDIUM_HEADER,
            color: theme[colorScheme].primary_white,
        },
        subText: {
            fontFamily: FONT_ENUM.REGULAR_HEADER,
            color: theme[colorScheme].light_gray_text,
            fontSize: 12,
            marginTop: 10,
        },
        input: {
            width: '100%',
            height: '100%',
            backgroundColor: theme[colorScheme].gray_text,
            borderRadius: 8,
            border: 'none',
        },
        customInput: {
            width: '100%',
            height: '100%',
            backgroundColor: theme[colorScheme].gray_text,
            borderRadius: 8,
            border: 'none',
        },
    });
